import React, { useState, useEffect } from 'react';
import { X, Save, Lock, Globe } from 'lucide-react';
import type { SiteTemplate, UpdateSiteTemplateRequest } from '../../schemas/validation';
import { cn } from '../../utils/cn';

type TemplateVisibility = 'private' | 'public';

function visibilityFromTemplate(template: SiteTemplate): TemplateVisibility {
  if (template.isPublic || template.isSystem) return 'public';
  return 'private';
}

interface EditTemplateModalProps {
  template: SiteTemplate;
  isSubmitting: boolean;
  onSave: (templateId: string, data: UpdateSiteTemplateRequest) => void;
  onCancel: () => void;
}

export const EditTemplateModal: React.FC<EditTemplateModalProps> = ({
  template,
  isSubmitting,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description || '');
  const [tags, setTags] = useState(template.tags || '');
  const [visibility, setVisibility] = useState<TemplateVisibility>(
    visibilityFromTemplate(template)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setName(template.name);
    setDescription(template.description || '');
    setTags(template.tags || '');
    setVisibility(visibilityFromTemplate(template));
    setErrors({});
  }, [template]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = 'Template name is required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSave(template.id, {
      name: name.trim(),
      description: description.trim() || undefined,
      tags: tags.trim() || undefined,
      isPublic: visibility === 'public',
      isSystem: false,
    });
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg max-w-lg w-full max-h-screen overflow-y-auto animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Template</h2>
            <p className="text-sm text-gray-500 mt-1">Update template details and Roary hints</p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-all duration-200"
            disabled={isSubmitting}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Template Name */}
          <div>
            <label
              htmlFor="templateName"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              id="templateName"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className={cn(
                'input-field',
                errors.name && 'border-red-300 focus:border-red-500 focus:ring-red-500'
              )}
              placeholder="Enter template name"
              disabled={isSubmitting}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Description (Roary Hint) */}
          <div>
            <label
              htmlFor="templateDescription"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Description / Roary Hint
            </label>
            <textarea
              id="templateDescription"
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="input-field resize-none"
              rows={4}
              placeholder="Describe what this template is ideal for..."
              disabled={isSubmitting}
            />
            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-xs text-amber-800">
                <strong>Roary uses this description</strong> to choose the right template for users.
                Be specific about the type of business or purpose.
              </p>
              <p className="text-xs text-amber-700 mt-1 italic">
                Examples: "Ideal for fitness studios, dance schools, or martial arts academies" or
                "Perfect for restaurants, cafes, and food delivery services"
              </p>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label
              htmlFor="templateTags"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Tags
            </label>
            <input
              id="templateTags"
              type="text"
              value={tags}
              onChange={e => setTags(e.target.value)}
              className="input-field"
              placeholder="e.g., business, portfolio, restaurant"
              disabled={isSubmitting}
            />
            <p className="mt-1 text-xs text-gray-500">
              Comma-separated tags to help categorize this template
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Visibility</label>
            <div className="space-y-3">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={visibility === 'private'}
                  onChange={() => setVisibility('private')}
                  className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  disabled={isSubmitting}
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Lock size={16} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">Private</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Only you can see and use this template
                  </p>
                </div>
              </label>

              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={visibility === 'public'}
                  onChange={() => setVisibility('public')}
                  className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  disabled={isSubmitting}
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Globe size={16} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">Public</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Anyone can see and use this template</p>
                </div>
              </label>
            </div>
          </div>

          {/* Template Info (read-only) */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Template Info
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Pages:</span>{' '}
                <span className="font-medium">{template.siteData.pages.length}</span>
              </div>
              <div>
                <span className="text-gray-500">Layouts:</span>{' '}
                <span className="font-medium">{template.siteData.layouts?.length || 0}</span>
              </div>
              <div>
                <span className="text-gray-500">Theme:</span>{' '}
                <span className="font-medium">{template.siteData.theme || 'Default'}</span>
              </div>
              <div>
                <span className="text-gray-500">Uses:</span>{' '}
                <span className="font-medium">{template.usageCount}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={cn(
                'btn-primary flex items-center space-x-2',
                isSubmitting && 'opacity-50 cursor-not-allowed'
              )}
              disabled={isSubmitting || !name.trim()}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 spinner"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
